import { InformationalBanner } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Form, Segment } from 'semantic-ui-react';

import { routes } from '../config/routes';
import { getInvoicePlanSaveErrorKey } from '../helpers/invoicePlanErrors';
import { billingIntervalsEqual, normalizeBillingInterval, normalizeInvoiceItemIds } from '../helpers/invoicePlans';
import { useChargesListener } from '../hooks/useCharges';
import { useInvoiceEditDetails, useInvoiceEditSelected } from '../hooks/useInvoiceEdit';
import { useOrganization } from '../hooks/useOrganizations';
import {
  invoiceEditDetails,
  invoiceEditItems,
  invoiceEditSelectionCleared,
  invoiceEditStudents,
} from '../redux/actions/billingActions';
import { PAGE_NAMES, useSegmentPage } from '../segment';

import { saveOrganizationInvoicePlan } from '../redux/actions/invoiceActions';

import PageHeader from '../Components/Shared/PageHeader';
import PageTitle from '../Components/Shared/PageTitle';

import StepConfirmAndSave from '../Components/Forms/Billing/StepConfirmAndSave';
import StepInvoiceItemList from '../Components/Forms/Billing/StepInvoiceItemList';
import StepInvoicePlanDetails from '../Components/Forms/Billing/StepInvoicePlanDetails';
import StepStudents from '../Components/Forms/Billing/StepStudents';

import '../Components/Forms/Billing/Billing.scss';

export default function InvoicePlanEdit() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isEditBillingIntervalEnabled } = useFlags();

  const organization = useOrganization();
  const invoicePlan = useInvoiceEditSelected();
  const details = useInvoiceEditDetails();
  useSegmentPage(PAGE_NAMES.invoicePlanEdit);
  useChargesListener(organization.id);

  const [invoicePlanSaved, setInvoicePlanSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Synchronous guard: a true double-click fires two clicks before React
  // re-renders, so a state flag alone is racy. A ref is set synchronously on
  // the first click, so the second click is a no-op. Prevents duplicate plans.
  const savingRef = useRef(false);
  const editSessionRef = useRef({
    planId: null,
    originalItems: [],
    originalCadence: null,
    convertedCadence: null,
    manuallyEdited: false,
  });
  const isEdit = !!invoicePlan;
  const title = isEdit ? t('Edit Recurring Plan') : t('Create Recurring Plan');

  if (!details?.id && isEdit) destructureInvoicePlanAndWriteToRedux(invoicePlan);

  let currentHash = location.hash || routes.billing.hashes.details;

  useEffect(() => {
    if (invoicePlanSaved) {
      navigate(routes.billing.plans.list);
    }
  }, [invoicePlanSaved, navigate]);

  return (
    <Segment basic>
      <PageHeader pageName={'Billing'} classes="billing" />
      <PageTitle title={title} />

      <Form autoComplete="off">
        {saveError && (
          <div role="alert">
            <InformationalBanner type="error" extraClasses="mb-4" data-testid="billing-save-error" label={saveError} />
          </div>
        )}
        {currentHash === routes.billing.hashes.details && (
          <StepInvoicePlanDetails onSave={onSaveDetails} isEdit={isEdit} />
        )}

        {currentHash === routes.billing.hashes.items && (
          <StepInvoiceItemList onSave={onSaveInvoiceItems} isInvoice={false} editSession={editSessionRef.current} />
        )}

        {currentHash === routes.billing.hashes.students && <StepStudents onSave={onSaveStudents} isInvoice={false} />}

        {currentHash === routes.billing.hashes.confirm && (
          <StepConfirmAndSave onSave={onSaveInvoicePlan} isInvoice={false} isSaving={isSaving} />
        )}
      </Form>
    </Segment>
  );

  function destructureInvoicePlanAndWriteToRedux(invoicePlan) {
    const { students, invoiceItemList, ...details } = invoicePlan;
    const billingInterval = normalizeBillingInterval(details.billingInterval);
    if (editSessionRef.current.planId !== invoicePlan.id) {
      const normalizedItems = normalizeInvoiceItemIds(invoiceItemList);
      editSessionRef.current = {
        planId: invoicePlan.id,
        originalItems: normalizedItems,
        originalCadence: billingInterval,
        convertedCadence: null,
        manuallyEdited: false,
      };
      onSaveInvoiceItems(normalizedItems);
    }
    const editableDetails = {
      ...details,
      billingInterval,
      dateStart: details.dateStart,
      dateEnd: details.dateEnd,
      dateDue: details.dateDue || details.startDate,
      dateSend: details.dateSend,
      oldBillingInterval: billingInterval,
      intervalChangeExpectedState: getIntervalChangeExpectedState({ ...details, invoiceItemList }),
    };
    onSaveDetails(editableDetails);
    onSaveStudents(students);
  }
  function onSaveDetails(details) {
    dispatch(invoiceEditDetails(details));
  }
  function onSaveInvoiceItems(invoiceItems) {
    dispatch(invoiceEditItems(invoiceItems));
  }
  function onSaveStudents(students) {
    dispatch(invoiceEditStudents(students));
  }

  async function onSaveInvoicePlan(invoicePlanDraft) {
    if (savingRef.current) return;
    const isUnflaggedCadenceChange =
      isEdit &&
      !isEditBillingIntervalEnabled &&
      !invoicePlan?.intervalTransitionRecovery &&
      !billingIntervalsEqual(invoicePlanDraft.billingInterval, invoicePlan.billingInterval);
    if (isUnflaggedCadenceChange) {
      setSaveError(t('billing.invoicePlan.saveFailed'));
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    try {
      await dispatch(saveOrganizationInvoicePlan(organization.id, invoicePlanDraft));
      dispatch(invoiceEditSelectionCleared());
      setInvoicePlanSaved(true);
    } catch (error) {
      savingRef.current = false;
      setIsSaving(false);
      setSaveError(t(getInvoicePlanSaveErrorKey(error)));
    }
  }
}

function getIntervalChangeExpectedState(invoicePlan) {
  return {
    status: invoicePlan.status,
    billingInterval: invoicePlan.billingInterval,
    dateDue: invoicePlan.dateDue ?? null,
    dateSend: invoicePlan.dateSend ?? null,
    dateServiceStart: invoicePlan.dateServiceStart ?? null,
    dateServiceEnd: invoicePlan.dateServiceEnd ?? null,
    dateEnd: invoicePlan.dateEnd ?? null,
    serviceDirection: invoicePlan.serviceDirection ?? null,
    isServiceDatesEnabled: invoicePlan.isServiceDatesEnabled ?? null,
    notificationDays: invoicePlan.notificationDays ?? null,
    location: invoicePlan.location ?? null,
    intervalChangePreviousBillingInterval: invoicePlan.intervalChangePreviousBillingInterval ?? null,
    intervalChangeEffectiveDue: invoicePlan.intervalChangeEffectiveDue ?? null,
    intervalChangeNotificationPending: invoicePlan.intervalChangeNotificationPending ?? null,
    intervalTransitionAttemptId: invoicePlan.intervalTransitionAttemptId ?? null,
    intervalTransitionRecovery: invoicePlan.intervalTransitionRecovery ?? null,
    intervalTransitionPhase: invoicePlan.intervalTransitionPhase ?? null,
    ...(Object.prototype.hasOwnProperty.call(invoicePlan, 'invoiceItemList')
      ? { invoiceItemList: cloneDeep(invoicePlan.invoiceItemList) }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(invoicePlan, 'total') ? { total: invoicePlan.total } : {}),
  };
}
